import { BellOff, BellRing, Server } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { api } from '../../api';
import { Section, StatCard, StatGrid } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { formatCount } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { AlertRow, IncidentTable, RefreshButton, SeverityBadge, severityOf } from './shared';

/** `/ops/alerts/:name`: every instance of one alert, its runbook and the incidents that name it. */
export function AlertPage() {
  const { t } = useTranslation('ops');
  const { name = '' } = useParams();
  useDocumentTitle(name);
  const alerts = useApi((signal) => api.listAlerts({ signal }), []);
  const incidents = useApi((signal) => api.listIncidents({ signal }), []);
  const crumbs = [{ label: t('crumb') }, { label: t('alerts.title'), to: '/ops/alerts' }, { label: name }];

  const mine = alerts.status === 'success' ? alerts.data.items.filter((a) => a.labels.alertname === name) : [];
  const firing = mine.filter((a) => a.status.state === 'active');
  const first = mine[0];
  const services = [...new Set(mine.map((a) => a.labels.service).filter((s): s is string => Boolean(s)))];
  const linked = incidents.status === 'success' ? incidents.data.items.filter((i) => i.related_alerts.includes(name)) : [];

  const reload = () => {
    alerts.reload();
    incidents.reload();
  };

  return (
    <>
      <PageHeader
        title={<span className="font-mono break-all">{name}</span>}
        crumbs={crumbs}
        subtitle={first?.annotations.summary ?? t('alerts.detail.subtitle')}
        actions={<RefreshButton onClick={reload} busy={alerts.status === 'loading' || incidents.status === 'loading'} />}
      />
      {alerts.status === 'loading' ? <Loading rows={3} /> : null}
      {alerts.status === 'error' ? <DataError error={alerts.error} onRetry={alerts.reload} title={t('alerts.loadFailed')} /> : null}
      {alerts.status === 'success' ? (
        <div className="space-y-6">
          <StatGrid className="xl:grid-cols-3">
            <StatCard icon={BellRing} label={t('alerts.detail.stat.firing')} value={formatCount(firing.length)} tone={firing.length ? 'warn' : 'ok'} />
            <StatCard icon={BellOff} label={t('alerts.detail.stat.silenced')} value={formatCount(mine.length - firing.length)} hint={t('alerts.stat.silencedHint')} />
            <StatCard
              icon={Server}
              label={t('alerts.detail.stat.severity')}
              value={first ? <SeverityBadge severity={severityOf(first)} withName /> : '—'}
            />
          </StatGrid>

          <Section title={t('alerts.detail.firing')} flush>
            {mine.length === 0 ? (
              <div className="px-5 pb-5">
                <EmptyState title={t('alerts.detail.firingEmpty')} />
              </div>
            ) : (
              <ul className="divide-y border-t">
                {mine.map((a) => (
                  <AlertRow key={a.fingerprint} a={a} linkName={false} />
                ))}
              </ul>
            )}
          </Section>

          {services.length ? (
            <Section title={t('alerts.detail.services')}>
              <ul className="flex flex-wrap gap-2">
                {services.map((s) => (
                  <li key={s}>
                    <Link to={`/ops/services/${encodeURIComponent(s)}`} className="inline-flex items-center gap-1.5 rounded-full border bg-card px-3 py-1 text-sm font-medium text-primary hover:bg-muted">
                      <Server className="size-3.5" aria-hidden="true" /> {s}
                    </Link>
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}

          <Section title={t('alerts.detail.incidents')} flush>
            {incidents.status === 'error' ? (
              <div className="px-5 pb-5">
                <DataError error={incidents.error} onRetry={incidents.reload} title={t('incidents.loadFailed')} />
              </div>
            ) : (
              <IncidentTable rows={linked} empty={<EmptyState title={t('alerts.detail.incidentsEmpty')} />} />
            )}
          </Section>
        </div>
      ) : null}
    </>
  );
}
